import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import { queryOptions } from "@tanstack/react-query"
import { z } from "zod"

import { queryClient } from "../components/providers/QueryProvider"
import { env } from "../env"
import { getRepositoryName } from "../routing"

export type GlossaryRule = z.infer<typeof GlossaryRule>
export const GlossaryRule = z.object({
  id: z.string(),
  sourceLocaleId: z.string(),
  targetLocaleId: z.string(),
  sourceValue: z.string(),
  targetValue: z.string(),
  createdAt: z.string(),
})

const GetGlossaryRulesResponse = z.object({
  results: z.array(GlossaryRule),
})

interface GetGlossaryRulesArgs {
  sourceLocaleId?: string
  targetLocaleId?: string
}

export async function getGlossaryRules(args: GetGlossaryRulesArgs = {}) {
  const { sourceLocaleId, targetLocaleId } = args

  const url = new URL("glossary/rules", env.PRISMIC_LOCALES_API_BASE_URL)
  url.searchParams.append("repository", getRepositoryName())

  if (sourceLocaleId) {
    url.searchParams.append("sourceLocaleId", sourceLocaleId)
  }

  if (targetLocaleId) {
    url.searchParams.append("targetLocaleId", targetLocaleId)
  }

  const { results } = await request(url, {
    method: "GET",
    credentials: getRequestCredentials(env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY),
    okType: GetGlossaryRulesResponse,
  })

  return results
}

export type GlossaryRulesQueryKey = ReturnType<
  (typeof glossaryRulesQueryKeys)[keyof typeof glossaryRulesQueryKeys]
>

export const glossaryRulesQueryKeys = {
  all: () => ["glossaryRules"] as const,
  list: (args: GetGlossaryRulesArgs) =>
    [...glossaryRulesQueryKeys.all(), args] as const,
}

export function getGlossaryRulesOptions(args: GetGlossaryRulesArgs = {}) {
  return queryOptions({
    queryKey: glossaryRulesQueryKeys.list(args),
    queryFn: () => getGlossaryRules(args),
  })
}

interface DeleteGlossaryRuleArgs {
  glossaryRuleId: string
}

export async function deleteGlossaryRule(args: DeleteGlossaryRuleArgs) {
  const { glossaryRuleId } = args

  const url = new URL(
    `glossary/rules/${glossaryRuleId}`,
    env.PRISMIC_LOCALES_API_BASE_URL,
  )
  url.searchParams.append("repository", getRepositoryName())

  await request(url, {
    method: "DELETE",
    credentials: getRequestCredentials(env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY),
    okType: z.unknown(),
  })
}

export interface UpdateGlossaryRuleBody {
  sourceValue?: string
  targetValue?: string
}

interface UpdateGlossaryRuleArgs {
  glossaryRuleId: string
  body: UpdateGlossaryRuleBody
}

export async function updateGlossaryRule(args: UpdateGlossaryRuleArgs) {
  const { glossaryRuleId, body } = args

  const url = new URL(
    `glossary/rules/${glossaryRuleId}`,
    env.PRISMIC_LOCALES_API_BASE_URL,
  )
  url.searchParams.append("repository", getRepositoryName())

  return await request(url, {
    method: "PATCH",
    body,
    credentials: getRequestCredentials(env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY),
    okType: GlossaryRule,
  })
}

export interface NewGlossaryRule {
  sourceLocaleId: string
  targetLocaleId: string
  sourceValue: string
  targetValue: string
}

interface CreateGlossaryRulesArgs {
  body: NewGlossaryRule[]
}

const CreateGlossaryRulesResponse = z.object({
  results: z.array(GlossaryRule),
})

export async function createGlossaryRules(args: CreateGlossaryRulesArgs) {
  const { body } = args

  const url = new URL(`glossary/rules`, env.PRISMIC_LOCALES_API_BASE_URL)
  url.searchParams.append("repository", getRepositoryName())

  const { results } = await request(url, {
    method: "POST",
    body,
    credentials: getRequestCredentials(env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY),
    okType: CreateGlossaryRulesResponse,
  })

  return results
}

export function addGlossaryRulesToQueryCache(glossaryRules: GlossaryRule[]) {
  for (const glossaryRule of glossaryRules) {
    const queryKey = glossaryRulesQueryKeys.list({
      sourceLocaleId: glossaryRule.sourceLocaleId,
      targetLocaleId: glossaryRule.targetLocaleId,
    })

    // If query is not in cache, skip.
    // We will load fresh data once user opens respective locale pair anyway.
    if (!queryClient.getQueryState(queryKey)) continue

    queryClient.setQueryData(
      queryKey,
      (oldGlossaryRules: GlossaryRule[] | undefined) => {
        const newGlossaryRules = [glossaryRule, ...(oldGlossaryRules ?? [])]
        return newGlossaryRules.sort(compareOrderOfGlossaryRules)
      },
    )
  }
}

function compareOrderOfGlossaryRules(a: GlossaryRule, b: GlossaryRule) {
  return Date.parse(b.createdAt) - Date.parse(a.createdAt)
}

export function removeGlossaryRuleFromQueryCache(glossaryRule: GlossaryRule) {
  const queryKey = glossaryRulesQueryKeys.list({
    sourceLocaleId: glossaryRule.sourceLocaleId,
    targetLocaleId: glossaryRule.targetLocaleId,
  })

  queryClient.setQueryData(
    queryKey,
    (oldGlossaryRules: GlossaryRule[] | undefined) =>
      oldGlossaryRules?.filter((rule) => rule.id !== glossaryRule.id),
  )
}

export function updateGlossaryRuleInQueryCache(
  glossaryRule: GlossaryRule,
  update: UpdateGlossaryRuleBody,
) {
  const queryKey = glossaryRulesQueryKeys.list({
    sourceLocaleId: glossaryRule.sourceLocaleId,
    targetLocaleId: glossaryRule.targetLocaleId,
  })

  queryClient.setQueryData(
    queryKey,
    (oldGlossaryRules: GlossaryRule[] | undefined) =>
      oldGlossaryRules?.map((rule) =>
        rule.id === glossaryRule.id ? { ...rule, ...update } : rule,
      ),
  )
}

export function cancelGlossaryRulesQueries(args: GetGlossaryRulesArgs) {
  return queryClient.cancelQueries({
    queryKey: glossaryRulesQueryKeys.list(args),
  })
}
